import Link from 'next/link';
import React, { FC, useEffect } from 'react';

import { Anchor, Avatar, Box, Card, Flex, Group, Stack, Text, Tooltip } from '@mantine/core';

import { Component, ComponentSection, ManufacturerProfile, PublishedStatus, SavedItemType } from 'models';
import { CompanyHelpers } from 'helpers/CompanyHelpers';
import { ComponentHelpers } from 'helpers/ComponentHelpers';

import { FeaturedBadge } from 'components/badges/FeaturedBadge';
import { LaunchBadge } from 'components/badges/LaunchBadge';
import { BoothNumber } from 'components/booth-number/BoothNumber';
import { SaveButton } from 'components/buttons/SaveButton';
import { CompanyLogo } from 'components/company-logo/CompanyLogo';
import { ComponentOverviewHitBadges } from 'components/component-overview/ComponentOverviewHitBadges';
import { ComponentOverviewHitDatasheetCable } from 'components/component-overview/ComponentOverviewHitDatasheet.Cable';
import { ComponentOverviewHitDatasheetOther } from 'components/component-overview/ComponentOverviewHitDatasheet.Other';
import { ComponentThumbnail } from 'components/thumbnail/ComponentThumbnail';
import { useCompanyProfile } from 'hooks/use-company-profile';
import { useComponentMeta } from 'hooks/use-component-meta';
import { useDistributorPrice } from 'hooks/use-distributor-price';
import { useProductsByIds } from 'hooks/use-products-by-ids';

import type { ComponentPricing } from '@/payload-types';

import { ComponentOverviewHitAveragePrice } from './ComponentOverviewHitAveragePrice';
import { ComponentOverviewHitDatasheet } from './ComponentOverviewHitDatasheet';
import { ComponentOverviewHitDistributorPrice } from './ComponentOverviewHitDistributorPrice';

const NB_COMPATIBLE_SHOWN = 5;

const ComponentOverviewHit: FC<{
    component: Component & { id?: string };
    distributorPrice?: ComponentPricing | null;
    showEmptyImage?: boolean;
    progressSections?: ComponentSection[];
    showBooth?: boolean;
    showIntercom?: boolean;
    isExternalLink?: boolean;
    hideLogo?: boolean;
    hideDatasheet?: boolean;
    showCompatibleWith?: boolean;
    featured?: boolean;
    onView?: () => void;
    onClick?: () => void;
    children?: React.ReactNode;
}> = ({
    component,
    distributorPrice: providedPrice,
    showEmptyImage,
    showBooth,
    showIntercom,
    isExternalLink,
    hideLogo,
    hideDatasheet,
    showCompatibleWith,
    featured,
    onView,
    onClick,
    children,
}) => {
    const { uniqueName, subtitle, slug } = useComponentMeta(component);

    const { company: manufacturer } = useCompanyProfile(component.manufacturer);

    const { distributorPrice: ownPrice } = useDistributorPrice(providedPrice === undefined ? component.id : null);
    const distributorPrice = providedPrice ?? ownPrice;

    const images = component?.images?.filter((image) => image.file) ?? [];

    useEffect(() => {
        onView?.();
    });

    const isExistingProduct = !!component.id;
    const url = isExistingProduct ? ComponentHelpers.urls.view(component.id, slug) : '#';

    return (
        <Card
            withBorder
            onClick={onClick}
            h="100%"
            pos="relative"
            style={{
                borderColor: featured ? 'var(--mantine-color-cyan-4)' : 'var(--mantine-color-gray-2)',
            }}
        >
            {featured && <FeaturedBadge pos="absolute" top={8} right={8} style={{ zIndex: 1 }} />}

            {(showEmptyImage || images.length > 0) && (
                <Card.Section p="xs" pos="relative">
                    <Anchor
                        component={Link}
                        href={url}
                        style={{ outline: 'none' }}
                        target={isExternalLink ? '_blank' : undefined}
                    >
                        <ComponentThumbnail
                            showEmpty
                            component={component}
                            style={{
                                width: '100%',
                                height: 100,
                            }}
                        />
                    </Anchor>

                    <LaunchBadge component={component} pos="absolute" bottom={8} left={16} />

                    {showBooth && manufacturer && (
                        <Group
                            gap={8}
                            align="start"
                            style={{
                                position: 'absolute',
                                top: featured ? 42 : 8,
                                left: 8,
                                right: 8,
                            }}
                        >
                            <BoothNumber company={manufacturer} showEventName />
                            <SaveButton
                                iconOnly
                                id={component.id}
                                name={component.name}
                                type={SavedItemType.COMPONENT}
                                actionIconProps={{ ml: 'auto' }}
                            />
                        </Group>
                    )}
                </Card.Section>
            )}

            {/* title + manufacturer */}
            <Flex justify="space-between" gap={8}>
                <Stack gap={4} pb="xs">
                    <Box>
                        <Anchor
                            component={Link}
                            href={url}
                            fw={600}
                            style={{ flex: 1 }}
                            c="#000"
                            target={isExternalLink ? '_blank' : undefined}
                        >
                            {uniqueName}
                        </Anchor>
                        <Text c="dimmed" fz="sm">
                            {subtitle}
                        </Text>
                    </Box>
                    {distributorPrice && <ComponentOverviewHitDistributorPrice distributorPrice={distributorPrice} />}
                    <ComponentOverviewHitAveragePrice component={component} />
                </Stack>

                {!hideLogo && manufacturer && <Manufacturer manufacturer={manufacturer} />}
            </Flex>

            {!hideDatasheet &&
                (component.type === 'cable' ? (
                    <ComponentOverviewHitDatasheetCable cable={component} />
                ) : component.type === 'other' ? (
                    <ComponentOverviewHitDatasheetOther component={component} />
                ) : (
                    <ComponentOverviewHitDatasheet component={component} />
                ))}

            {/* <Space h="md" /> */}

            {/* <Box>
                <OrderButton componentId={component.id} isLight />
            </Box> */}

            {isExistingProduct && (
                <ComponentOverviewHitBadges
                    component={component}
                    showIntercom={showIntercom}
                    isExternalLink={isExternalLink}
                />
            )}

            {showCompatibleWith && !!component.compatibleWith?.length && (
                <CompatibleWith compatibleIds={component.compatibleWith} />
            )}

            {children}
        </Card>
    );
};

const Manufacturer: FC<{ manufacturer: ManufacturerProfile; isExternalLink?: boolean }> = ({
    manufacturer,
    isExternalLink,
}) => {
    if (manufacturer.status === PublishedStatus.PUBLISHED) {
        return (
            <Tooltip label="View profile">
                <Link href={CompanyHelpers.urls.view(manufacturer.slug)} target={isExternalLink ? '_blank' : undefined}>
                    <CompanyLogo logos={manufacturer.logos} width={50} fallback={manufacturer.name} />
                </Link>
            </Tooltip>
        );
    }

    return <CompanyLogo logos={manufacturer.logos} width={50} fallback={manufacturer.name} />;
};

const CompatibleWith = ({ compatibleIds }: { compatibleIds: string[] }) => {
    const { components: compatibleProducts } = useProductsByIds(compatibleIds);

    return (
        <Box pt="xs">
            <Text fz="xs" c="dimmed" mb={4}>
                Compatible with
            </Text>
            <Group gap={8}>
                {compatibleProducts.slice(0, NB_COMPATIBLE_SHOWN).map((p) => (
                    <Tooltip key={p.id} label={p.name} withArrow>
                        <Avatar
                            component={Link}
                            href={ComponentHelpers.urls.view(p.id)}
                            target="_blank"
                            size={40}
                            radius="sm"
                            style={{
                                border: '1px solid var(--mantine-color-gray-2)',
                            }}
                            styles={{
                                placeholder: {
                                    background: 'var(--mantine-color-gray-0)',
                                },
                            }}
                        >
                            <ComponentThumbnail
                                component={p}
                                style={{ width: 28, height: 28, mixBlendMode: 'multiply' }}
                                showEmpty
                            />
                        </Avatar>
                    </Tooltip>
                ))}
                {compatibleProducts.length > NB_COMPATIBLE_SHOWN && (
                    <Text fz="xs" c="dimmed">
                        +{compatibleProducts.length - NB_COMPATIBLE_SHOWN}
                    </Text>
                )}
            </Group>
        </Box>
    );
};

export { ComponentOverviewHit };
